import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ragPluginApi } from '@/api/ragPlugin.api';
import { formatTimestamp } from '@/utils/utils';

export default function RAGDetailPage() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['rag-detail', id],
    enabled: !!id,
    queryFn: () => ragPluginApi.getRAG(id),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Card>
            <CardContent className="py-10 text-center space-y-3">
              <p className="text-sm text-destructive">实例加载失败</p>
              <Button
                variant="outline"
                onClick={() => navigate('/plugins/rags/catalog?tab=instance')}
              >
                返回知识库组
              </Button>
              <Button onClick={() => refetch()}>重试</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const paramEntries = Object.entries(data.classParams || {});

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{data.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">实例详情，可在智能体插件中引用。</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/plugins/rags/catalog?tab=instance')}>
            返回知识库组
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">基本信息</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground mb-1">资源 ID（raggroups.id）</div>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {data.id}
              </code>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Provider</div>
              <div className="font-mono text-xs">{data.provider || 'default'}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">所属 RAG 类</div>
              <div className="font-mono text-xs">{data.classId || '—'}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">说明</div>
              <div>{data.description || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">创建时间</div>
              <div>{formatTimestamp(data.createdAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">更新时间</div>
              <div>{formatTimestamp(data.updatedAt)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">引用的知识库集合</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.collections || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">未配置集合引用。</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.collections.map((cid) => (
                  <li key={cid} className="flex flex-wrap gap-2 rounded-md border px-3 py-2">
                    <span className="font-mono text-xs break-all">{cid}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">类参数</CardTitle>
          </CardHeader>
          <CardContent>
            {paramEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">无类参数记录。</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {paramEntries.map(([k, v]) => (
                  <li key={k} className="flex gap-2 rounded-md border px-3 py-2">
                    <span className="font-mono text-xs shrink-0">{k}</span>
                    <span className="text-muted-foreground break-all">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
